import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Checkbox } from 'ui-components/components/checkbox';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import { pickListGroupings, pickListSortings, pickListWarehouseLocations, pickListSkuSources } from './constants';
import messages from './messages';
import { emptyObject } from 'ui-components/default-props/empty';
import { SellerSettings } from '../../../../../enums/seller-settings/seller-settings';
import { useEnableLotTracking } from '../../../../../feature-flags/hooks/use-enable-lot-tracking';
import {
  usePickListGrouping,
  usePickListSorting,
  usePickListImages,
  usePickListMasterSku,
  usePickListOrderNumbers,
  usePickListZeroQuantity,
  usePickListInventoryCounts,
  usePickListWarehouseLocation,
  usePickListIncludeLotId,
  usePickListHideItemOptions,
} from '../../../../../hooks/use-seller-setting';
import { booleanToSkuSourceString, skuSourceStringToBoolean } from '../../../../../utils/sku-source-conversion';
import { findMatchingSettingEnum } from '../helpers';
import sharedMessages from '../shared-messages';
import style from '../style.module.less';

const PickListDocumentOptionsModal = ({ onInit, settingsState = emptyObject, onDropdownChange, onCheckboxChange }) => {
  const intl = useIntl();
  const pickListGroupingSetting = usePickListGrouping();
  const pickListSortingSetting = usePickListSorting();
  const pickListImages = usePickListImages();
  const pickListOrderNumbers = usePickListOrderNumbers();
  const pickListMasterSku = usePickListMasterSku();
  const pickListZeroQuantity = usePickListZeroQuantity();
  const pickListInventoryCounts = usePickListInventoryCounts();
  const pickListWarehouseLocationSetting = usePickListWarehouseLocation();
  const pickListIncludeLotIdSetting = usePickListIncludeLotId();
  const pickListHideItemOptionsSetting = usePickListHideItemOptions();

  const initialSettingsState = useMemo(
    () => ({
      [SellerSettings.pickListGrouping.key]: pickListGroupingSetting,
      [SellerSettings.pickListSorting.key]: pickListSortingSetting,
      [SellerSettings.pickListImages.key]: pickListImages,
      [SellerSettings.pickListOrderNumbers.key]: pickListOrderNumbers,
      [SellerSettings.pickListMasterSku.key]: pickListMasterSku,
      [SellerSettings.pickListZeroQuantity.key]: pickListZeroQuantity,
      [SellerSettings.pickListInventoryCounts.key]: pickListInventoryCounts,
      [SellerSettings.pickListWarehouseLocation.key]: pickListWarehouseLocationSetting,
      [SellerSettings.pickListIncludeLotId.key]: pickListIncludeLotIdSetting,
      [SellerSettings.pickListHideItemOptions.key]: pickListHideItemOptionsSetting,
    }),
    [
      pickListGroupingSetting,
      pickListSortingSetting,
      pickListImages,
      pickListOrderNumbers,
      pickListMasterSku,
      pickListZeroQuantity,
      pickListInventoryCounts,
      pickListWarehouseLocationSetting,
      pickListIncludeLotIdSetting,
      pickListHideItemOptionsSetting,
    ],
  );

  useEffect(() => {
    onInit(initialSettingsState);
  }, [onInit, initialSettingsState]);
  const pickListGrouping = settingsState[SellerSettings.pickListGrouping.key] || initialSettingsState[SellerSettings.pickListGrouping.key];
  const pickListSorting = settingsState[SellerSettings.pickListSorting.key] || initialSettingsState[SellerSettings.pickListSorting.key];
  const printImages = settingsState[SellerSettings.pickListImages.key] ?? initialSettingsState[SellerSettings.pickListImages.key];
  const includeOrderNumbers =
    settingsState[SellerSettings.pickListOrderNumbers.key] ?? initialSettingsState[SellerSettings.pickListOrderNumbers.key];
  const useProductSku = settingsState[SellerSettings.pickListMasterSku.key] ?? initialSettingsState[SellerSettings.pickListMasterSku.key];

  const hideZeroQuantity =
    settingsState[SellerSettings.pickListZeroQuantity.key] ?? initialSettingsState[SellerSettings.pickListZeroQuantity.key];
  const inventoryCounts =
    settingsState[SellerSettings.pickListInventoryCounts.key] ?? initialSettingsState[SellerSettings.pickListInventoryCounts.key];
  const pickListWarehouseLocation =
    settingsState[SellerSettings.pickListWarehouseLocation.key] || initialSettingsState[SellerSettings.pickListWarehouseLocation.key];
  const includeLotIdInPickList =
    settingsState[SellerSettings.pickListIncludeLotId.key] ?? initialSettingsState[SellerSettings.pickListIncludeLotId.key];
  const hideItemOptions =
    settingsState[SellerSettings.pickListHideItemOptions.key] ?? initialSettingsState[SellerSettings.pickListHideItemOptions.key];

  const isLotTrackingEnabled = useEnableLotTracking();

  return (
    <>
      <BannerMessageV2 content={<FormattedMessage {...sharedMessages.affectsAllUsersWarning} />} status={BannerStatus.Info} />
      <div className={style.description}>
        <FormattedMessage {...messages.pickListOptionsDescription} />
      </div>
      <div className={style['print-options-grid']}>
        <FormattedMessage {...messages.summarizeByLabel} />
        <div>
          <DropdownMenu dropdownMenuShouldHaveWidthOfButton fullWidth id={SellerSettings.pickListGrouping.key} variant='form'>
            <DropdownMenuToggler title={intl.formatMessage(findMatchingSettingEnum(pickListGroupings, pickListGrouping).messageDescriptor)}>
              {intl.formatMessage(findMatchingSettingEnum(pickListGroupings, pickListGrouping).messageDescriptor)}
            </DropdownMenuToggler>
            {pickListGroupings.map(({ messageDescriptor, value }) => (
              <DropdownMenuItem key={value} onClick={() => onDropdownChange(SellerSettings.pickListGrouping.key, value)}>
                {intl.formatMessage(messageDescriptor)}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </div>
        <FormattedMessage {...messages.orderItemsByLabel} />
        <div>
          <DropdownMenu dropdownMenuShouldHaveWidthOfButton fullWidth id={SellerSettings.pickListSorting.key} variant='form'>
            <DropdownMenuToggler title={intl.formatMessage(findMatchingSettingEnum(pickListSortings, pickListSorting).messageDescriptor)}>
              {intl.formatMessage(findMatchingSettingEnum(pickListSortings, pickListSorting).messageDescriptor)}
            </DropdownMenuToggler>
            {pickListSortings.map(({ messageDescriptor, value }) => (
              <DropdownMenuItem key={value} onClick={() => onDropdownChange(SellerSettings.pickListSorting.key, value)}>
                {intl.formatMessage(messageDescriptor)}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </div>
        <FormattedMessage {...messages.productImagesLabel} />
        <div>
          <Checkbox
            checked={printImages}
            label={<FormattedMessage {...messages.printProductImages} />}
            name={SellerSettings.pickListImages.key}
            onChange={onCheckboxChange}
          />
        </div>
        <FormattedMessage {...messages.orderNumberLabel} />
        <div>
          <Checkbox
            checked={includeOrderNumbers}
            label={<FormattedMessage {...messages.includeOrderNumber} />}
            name={SellerSettings.pickListOrderNumbers.key}
            onChange={onCheckboxChange}
          />
        </div>
        <FormattedMessage {...messages.productSkuLabel} />
        <div>
          <DropdownMenu dropdownMenuShouldHaveWidthOfButton fullWidth id={SellerSettings.pickListMasterSku.key} variant='form'>
            <DropdownMenuToggler
              title={intl.formatMessage(
                findMatchingSettingEnum(pickListSkuSources, booleanToSkuSourceString(useProductSku)).messageDescriptor,
              )}
            >
              {intl.formatMessage(findMatchingSettingEnum(pickListSkuSources, booleanToSkuSourceString(useProductSku)).messageDescriptor)}
            </DropdownMenuToggler>
            {pickListSkuSources.map(({ messageDescriptor, value }) => (
              <DropdownMenuItem
                key={value}
                onClick={() => onDropdownChange(SellerSettings.pickListMasterSku.key, skuSourceStringToBoolean(value))}
              >
                {intl.formatMessage(messageDescriptor)}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </div>
        <FormattedMessage {...messages.hideZeroQuantity} />
        <div>
          <Checkbox
            checked={hideZeroQuantity}
            label={<FormattedMessage {...messages.useHideZeroQuantity} />}
            name={SellerSettings.pickListZeroQuantity.key}
            onChange={onCheckboxChange}
          />
        </div>
        <FormattedMessage {...messages.inventoryCounts} />
        <div>
          <Checkbox
            checked={inventoryCounts}
            label={<FormattedMessage {...messages.useInventoryCounts} />}
            name={SellerSettings.pickListInventoryCounts.key}
            onChange={onCheckboxChange}
          />
        </div>
        <FormattedMessage {...messages.warehouseLocation} />
        <div>
          <DropdownMenu dropdownMenuShouldHaveWidthOfButton fullWidth id={SellerSettings.pickListWarehouseLocation.key} variant='form'>
            <DropdownMenuToggler
              title={intl.formatMessage(findMatchingSettingEnum(pickListWarehouseLocations, pickListWarehouseLocation).messageDescriptor)}
            >
              {intl.formatMessage(findMatchingSettingEnum(pickListWarehouseLocations, pickListWarehouseLocation).messageDescriptor)}
            </DropdownMenuToggler>
            {pickListWarehouseLocations.map(({ messageDescriptor, value }) => (
              <DropdownMenuItem key={value} onClick={() => onDropdownChange(SellerSettings.pickListWarehouseLocation.key, value)}>
                {intl.formatMessage(messageDescriptor)}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </div>
        {isLotTrackingEnabled ? (
          <>
            <FormattedMessage {...messages.includeLotId} />
            <div>
              <Checkbox
                checked={includeLotIdInPickList}
                label={<FormattedMessage {...messages.includeLotId} />}
                name={SellerSettings.pickListIncludeLotId.key}
                onChange={onCheckboxChange}
              />
            </div>
          </>
        ) : null}
        <FormattedMessage {...messages.hideItemOptions} />
        <div>
          <Checkbox
            checked={hideItemOptions}
            label={<FormattedMessage {...messages.hideItemOptions} />}
            name={SellerSettings.pickListHideItemOptions.key}
            onChange={onCheckboxChange}
          />
        </div>
      </div>
    </>
  );
};

PickListDocumentOptionsModal.propTypes = {
  onInit: PropTypes.func.isRequired,
  onDropdownChange: PropTypes.func.isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
  settingsState: PropTypes.object,
};

export default PickListDocumentOptionsModal;
